// The landing page's fourth screen: one job, start to finish, as five tabs
// (founder, 2026-11-02: "more appealing, colourful and analytical").
//
// Each tab has its own soft accent, and each panel pairs a real screen
// (LandingShot.js: our own components with fictional data) with the numbers
// the product really computes for it, drawn as small charts. Nothing here is
// invented: the numbers are one sample job's own, as the ranking code works
// them out on the fixture catalogue (Node Backend services/jobSearch, fit
// features; "Software Engineer II, Sample Labs" scores 88), the extension's
// panel (8 of 8 filled, each read back from the page), the coach's analysis
// (readiness 88; "Numbers in it" is High/Medium/Low and "Shows what you did"
// Strong/Moderate/Weak, routes/zagaRoutes.js), and a run's stages. Every
// panel is marked "Example".
//
// Real ARIA tabs: the arrow keys, Home and End move between all five, each tab
// names the panel it controls, and each panel is labelled by its tab. No inbox
// reading anywhere: the mail tracker is switched off (src/features.js).
import React, { useRef, useState } from 'react';
import LandingShot from './LandingShot';

// The sample job every number below belongs to.
export const SAMPLE_JOB = { title: 'Software Engineer II', company: 'Sample Labs', score: 88 };

export const STEPS = [
  { key: 'find', label: 'Find', tone: 'teal', shot: 'find', caption: 'Search the way you talk. Every job gets a score, and the reasons for it.' },
  { key: 'resume', label: 'Resume', tone: 'indigo', shot: 'resume', caption: 'Each change comes from your own experience. You apply it or skip it.' },
  { key: 'apply', label: 'Apply', tone: 'green', shot: 'apply', caption: 'Filled in your own browser. What only you can answer is left for you.' },
  { key: 'interview', label: 'Interview', tone: 'amber', shot: 'interview', caption: 'Your real stories, scored, with what to add before the interview.' },
  { key: 'track', label: 'Track', tone: 'slate', shot: 'track', caption: 'Every application, from saved to sent, with the thank-you page.' },
];

function Ring({ value, label }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <div className="lp-ring" role="img" aria-label={`${label}: ${value} out of 100`}>
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle className="lp-ring-track" cx="32" cy="32" r={r} />
        <circle className="lp-ring-fill" cx="32" cy="32" r={r} strokeDasharray={`${(value / 100) * c} ${c}`} transform="rotate(-90 32 32)" />
      </svg>
      <span className="lp-ring-num" aria-hidden="true">{value}</span>
    </div>
  );
}

// A bar out of `of` (100 for a percent). `words` is what it says beside it.
function Bar({ label, value, of = 100, words }) {
  return (
    <li className="lp-bar">
      <span className="lp-bar-head"><span>{label}</span><span className="lp-bar-val">{words || `${value}%`}</span></span>
      <span className="lp-bar-track" aria-hidden="true"><span className="lp-bar-fill" style={{ width: `${Math.round((value / of) * 100)}%` }} /></span>
    </li>
  );
}

// A three-step rating (Low / Medium / High), as three segments.
function Rating({ label, word, level }) {
  return (
    <li className="lp-bar">
      <span className="lp-bar-head"><span>{label}</span><span className="lp-bar-val">{word}</span></span>
      <span className="lp-seg" aria-hidden="true">
        {[1, 2, 3].map((i) => <span key={i} className={i <= level ? 'is-on' : ''} />)}
      </span>
    </li>
  );
}

function Insight({ step }) {
  switch (step) {
    case 'find':
      return (
        <>
          <div className="lp-ins-top">
            <Ring value={SAMPLE_JOB.score} label="Match" />
            <div><p className="lp-ins-title">{SAMPLE_JOB.title}</p><p className="lp-ins-sub">{SAMPLE_JOB.company} · New York, NY</p></div>
          </div>
          <p className="lp-ins-h">Why this score</p>
          <ul className="lp-bars">
            <Bar label="Similar to your resume" value={95} />
            <Bar label="What you said you want" value={100} />
            <Bar label="Skills it asks for" value={75} />
            <Bar label="Experience level" value={50} words="One step above you" />
          </ul>
        </>
      );
    case 'resume':
      return (
        <>
          <p className="lp-ins-h">Skills the job lists</p>
          <p className="lp-ins-big"><span>3</span> of 4 on your resume</p>
          <ul className="lp-bars"><Bar label="Python, AWS, Kubernetes" value={3} of={4} words="3 of 4" /></ul>
          <p className="lp-ins-sub">Not on it: Java. It is never added for you.</p>
        </>
      );
    case 'apply':
      return (
        <>
          <p className="lp-ins-h">This application</p>
          <ul className="lp-bars">
            <Bar label="Filled" value={8} of={8} words="8 of 8" />
            <Bar label="Read back from the page" value={8} of={8} words="8 of 8" />
          </ul>
          <p className="lp-ins-sure">It never types a password.</p>
        </>
      );
    case 'interview':
      return (
        <>
          <div className="lp-ins-top">
            <Ring value={88} label="Readiness" />
            <div><p className="lp-ins-title">Settled a disagreement on the launch plan</p><p className="lp-ins-sub">Readiness, scored by the coach</p></div>
          </div>
          <ul className="lp-bars">
            <Rating label="Numbers in it" word="Medium" level={2} />
            <Rating label="Shows what you did" word="Strong" level={3} />
          </ul>
        </>
      );
    default:
      return (
        <>
          <p className="lp-ins-h">Backend Engineer · Test Systems</p>
          <ol className="lp-steps-line">
            <li className="is-done">Saved</li>
            <li className="is-done">Filled</li>
            <li className="is-done">Sent</li>
          </ol>
          <p className="lp-ins-sub">Sent today · thank-you page saved</p>
        </>
      );
  }
}

export default function LandingSteps() {
  const [active, setActive] = useState(STEPS[0].key);
  const tabs = useRef({});

  const select = (key, focus = false) => {
    setActive(key);
    if (focus) tabs.current[key]?.focus();
  };

  const onKeyDown = (event) => {
    const at = STEPS.findIndex((s) => s.key === active);
    const last = STEPS.length - 1;
    const to = {
      ArrowRight: at === last ? 0 : at + 1,
      ArrowLeft: at === 0 ? last : at - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (to === undefined) return;
    event.preventDefault();
    select(STEPS[to].key, true);
  };

  return (
    <section className="lp-steps" id="how-it-works" aria-labelledby="steps-title">
      <div className="lp-wrap">
        <div className="lp-steps-head">
          <h2 id="steps-title" className="lp-display">From first search to sent application</h2>
          <p className="lp-steps-sub">One job, start to finish. You stay in charge at every step.</p>
        </div>

        <div className="lp-steps-tabs" role="tablist" aria-label="Steps" onKeyDown={onKeyDown}>
          {STEPS.map((s) => {
            const on = s.key === active;
            return (
              <button
                key={s.key}
                ref={(el) => { tabs.current[s.key] = el; }}
                type="button"
                role="tab"
                id={`lp-step-tab-${s.key}`}
                aria-selected={on}
                aria-controls={`lp-step-panel-${s.key}`}
                tabIndex={on ? 0 : -1}
                className={`lp-steps-tab${on ? ' is-on' : ''}`}
                data-tone={s.tone}
                onClick={() => select(s.key)}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <div className="lp-steps-stage">
          {STEPS.map((s) => {
            const on = s.key === active;
            return (
              <div
                key={s.key}
                role="tabpanel"
                id={`lp-step-panel-${s.key}`}
                aria-labelledby={`lp-step-tab-${s.key}`}
                tabIndex={0}
                hidden={!on}
                className="lp-steps-panel"
                data-tone={s.tone}
              >
                <div className="lp-steps-pair">
                  <LandingShot name={s.shot} />
                  <aside className="lp-ins" aria-label={`${s.label}, in numbers`}>
                    <Insight step={s.key} />
                    <p className="lp-ins-mark">Example · sample data</p>
                  </aside>
                </div>
                <p className="lp-steps-caption">{s.caption}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
